// Turn a pasted share URL / embed snippet into a safe iframe src.
// Hosts are re-validated server-side against the KB embed allow-list.

const YT = /(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/i;
const VIMEO = /vimeo\.com\/(?:video\/)?(\d+)(?:\/([\w-]+))?/i;
const LOOM = /loom\.com\/(?:share|embed)\/([\w-]+)/i;
const SYNTHESIA = /share\.synthesia\.io\/(?!embeds\/)([\w-]{8,})/i;
const SCRIBE = /scribehow\.com\/(?:shared|embed)\/([\w-]+)/i;

// A URL that is ALREADY in its host's embed form is passed through untouched.
// Rewriting these used to drop the query string, which carries the privacy
// hash on unlisted Vimeo videos (`?h=…`) and player config elsewhere — the
// video then failed to load, or loaded the host's "page not found" screen.
const EMBED_FORMS = [
  /^https?:\/\/player\.vimeo\.com\/video\//i,
  /^https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\//i,
  /\/embeds?\//i,
  /\/embed-player\//i,
];

/** Extract the src from a pasted <iframe …> snippet, else return the input. */
export function srcFromSnippet(input) {
  const raw = (input || "").trim();
  const m = /<iframe[^>]*\ssrc=["']([^"']+)["']/i.exec(raw);
  return m ? m[1] : raw;
}

/** Best-effort conversion of a share link into its embeddable URL. */
export function toEmbedSrc(input) {
  const url = srcFromSnippet(input);
  if (!url) return "";
  if (EMBED_FORMS.some((re) => re.test(url))) return url;
  let m = YT.exec(url);
  if (m) return `https://www.youtube-nocookie.com/embed/${m[1]}`;
  m = VIMEO.exec(url);
  if (m) {
    // vimeo.com/123456789/abc123 — the second segment is the unlisted hash.
    return `https://player.vimeo.com/video/${m[1]}${m[2] ? `?h=${m[2]}` : ""}`;
  }
  m = LOOM.exec(url);
  if (m) return `https://www.loom.com/embed/${m[1]}`;
  m = SYNTHESIA.exec(url);
  if (m) return `https://share.synthesia.io/embeds/videos/${m[1]}`;
  m = SCRIBE.exec(url);
  if (m) return `https://scribehow.com/embed/${m[1]}`;
  return url.startsWith("http") || url.startsWith("/api/kb/assets/") ? url : "";
}

/** Is this src allowed by the current embed host list? */
export function hostAllowed(src, hosts) {
  if (!src) return false;
  if (src.startsWith("/api/kb/assets/")) return true;
  try {
    const host = new URL(src).hostname.toLowerCase();
    return (hosts || []).some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}
